import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type {
  TrainingCenterState,
  TrainingEvent,
  EventRegistration,
  LearnerProgress,
  Achievement,
  TeamMember,
  PricingRule,
} from "@/types/learningCenter";

// Mock Data
const mockEvents: TrainingEvent[] = [
  {
    id: "1",
    name: "Service Essentials",
    date: "2025-02-15",
    type: "In-Person",
    location: "Addison, TX",
    status: "registered",
    hasEnhancement: true,
    prework: {
      required: true,
      items: [
        {
          name: "DISC Assessment",
          type: "assessment",
          completed: true,
          locked: false,
        },
        {
          name: "Prep Videos (30 min)",
          type: "video",
          completed: false,
          locked: false,
          durationMinutes: 30,
        },
        {
          name: "Enhanced Module 1",
          type: "course",
          completed: false,
          locked: false,
        },
        {
          name: "Enhanced Module 2",
          type: "course",
          completed: false,
          locked: true,
        },
      ],
      deadline: "2025-02-08",
      progress: 25,
    },
  },
  {
    id: "2",
    name: "Sales Excellence",
    date: "2025-03-10",
    type: "Virtual",
    status: "available",
    hasEnhancement: false,
  },
];

const mockProgress: LearnerProgress = {
  classesCompleted: 12,
  certificationsEarned: 3,
  learningStreak: 5,
  totalPoints: 850,
};

const mockAchievements: Achievement[] = [
  {
    id: "1",
    name: "Quick Learner",
    description: "Completed pre-work in record time",
    icon: "award",
    earnedDate: "2025-01-15",
  },
  {
    id: "2",
    name: "Rising Star",
    description: "5 classes completed this quarter",
    icon: "star",
    earnedDate: "2025-01-20",
  },
];

const mockTeamMembers: TeamMember[] = [
  {
    id: "1",
    name: "Bob Johnson",
    role: "Service Technician",
    upcomingTraining: ["Service Essentials (Feb 15)"],
    completedTraining: 12,
    hasReinforcement: true,
    reinforcementProgress: 30,
    reinforcementWeek: 4,
    reinforcementTotalWeeks: 12,
  },
  {
    id: "2",
    name: "Sarah Miller",
    role: "Sales Representative",
    upcomingTraining: ["Sales Excellence (Mar 10)"],
    completedTraining: 8,
    hasReinforcement: false,
  },
  {
    id: "3",
    name: "Mike Wilson",
    role: "Service Technician",
    upcomingTraining: ["Service Essentials (Feb 15)", "HVAC Advanced (Apr 5)"],
    completedTraining: 15,
    hasReinforcement: true,
    reinforcementProgress: 75,
    reinforcementWeek: 9,
    reinforcementTotalWeeks: 12,
  },
];

const initialState: TrainingCenterState = {
  events: mockEvents,
  registrations: [],
  learnerProgress: mockProgress,
  achievements: mockAchievements,
  teamMembers: mockTeamMembers,
  pricingRules: [],
  userTier: 2, // Tier 2: Total Access (default for demo)
  loading: false,
  error: null,
};

const trainingCenterSlice = createSlice({
  name: "trainingCenter",
  initialState,
  reducers: {
    registerForEvent: (state, action: PayloadAction<EventRegistration>) => {
      state.registrations.push(action.payload);
      const event = state.events.find((e) => e.id === action.payload.eventId);
      if (event) {
        event.status = "registered";
      }
    },
    completePreworkItem: (
      state,
      action: PayloadAction<{ eventId: string; itemName: string }>,
    ) => {
      const event = state.events.find((e) => e.id === action.payload.eventId);
      if (event?.prework) {
        const item = event.prework.items.find(
          (i) => i.name === action.payload.itemName,
        );
        if (item) {
          item.completed = true;

          // Recalculate progress
          const totalItems = event.prework.items.length;
          const completedItems = event.prework.items.filter(
            (i) => i.completed,
          ).length;
          event.prework.progress = Math.round(
            (completedItems / totalItems) * 100,
          );

          // Unlock next item
          const currentIndex = event.prework.items.findIndex(
            (i) => i.name === action.payload.itemName,
          );
          if (currentIndex < event.prework.items.length - 1) {
            event.prework.items[currentIndex + 1].locked = false;
          }
        }
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const { registerForEvent, completePreworkItem, setLoading, setError } =
  trainingCenterSlice.actions;
export default trainingCenterSlice.reducer;
